import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { AiSearchPatternViewStore } from "../../store";
import { AiSearchPatternView } from "../view/AiSearchPatternView";
import { AiSearchPatternUpdateStore } from "../../store";
import { AiSearchPatternUpdate } from "../update/AiSearchPatternUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type AiSearchPatternModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const AiSearchPatternModalContent: React.FC<AiSearchPatternModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new AiSearchPatternUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new AiSearchPatternViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <AiSearchPatternView
            aiSearchPatternViewStore={
              storeInfo.store as AiSearchPatternViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <AiSearchPatternUpdate
            aiSearchPatternUpdateStore={
              storeInfo.store as AiSearchPatternUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
